import React, { useEffect, useMemo, useState } from 'react';
import { BONUS_LIST } from '../BonusList';
import { getStoredBonusDisplay } from '../../utils/bonusConfig';

const MATCH_PRESENCE_BONUS_VALUES = new Set(['match_sett', 'match_ple']);
const normalizeStoredBonusValue = (value) => String(value || '').replace(/_ext$/, '');
const BONUS_LOOKUP = (value) => BONUS_LIST.find((bonus) => bonus.value === value);

function ParticipationManager({
  participations,
  onEditParticipation,
  onDeleteParticipation,
  onParticipationSelect,
  selectedParticipationsForEdit,
  editingParticipationId,
  onEnableBulkEdit,
  participationMode = 'live',
  draftCount = 0,
  liveCount = 0,
  isPublishingDrafts = false,
  onPublishDrafts,
  showData = null,
  wrestlerScoresById = {},
  isLiveBonusLocked = false,
  liveBonusUnlockLabel = '',
  moveTargets = [],
  moveTargetShowId = '',
  onMoveTargetChange,
  onMoveSelectedParticipations,
  isMovingParticipations = false,
  bonusSuggestionsByWrestler = {}
}) {
  const [sortMode, setSortMode] = useState(participationMode === 'draft' ? 'score' : 'name');

  useEffect(() => {
    setSortMode(participationMode === 'draft' ? 'score' : 'name');
  }, [participationMode]);

  const sortedParticipations = useMemo(() => {
    const items = [...participations];

    return items.sort((first, second) => {
      if (sortMode === 'score') {
        const firstTotalPoints = Number(first.totalPoints) || 0;
        const secondTotalPoints = Number(second.totalPoints) || 0;
        if (firstTotalPoints !== secondTotalPoints) {
          return secondTotalPoints - firstTotalPoints;
        }

        const firstScore = Number(wrestlerScoresById[first.wrestlerId]) || 0;
        const secondScore = Number(wrestlerScoresById[second.wrestlerId]) || 0;
        if (firstScore !== secondScore) return secondScore - firstScore;
      }

      return first.wrestlerName.localeCompare(second.wrestlerName);
    });
  }, [participations, sortMode, wrestlerScoresById]);

  const canBulkEditBonuses = !(participationMode === 'live' && isLiveBonusLocked);
  const canMoveParticipations = participationMode === 'live' && moveTargets.length > 0;
  const suggestedBonusCount = useMemo(
    () =>
      participations.filter(
        (participation) =>
          Array.isArray(bonusSuggestionsByWrestler[participation.wrestlerId]) &&
          bonusSuggestionsByWrestler[participation.wrestlerId].length > 0
      ).length,
    [bonusSuggestionsByWrestler, participations]
  );

  const handleSelectableRowKeyDown = (event, participationId) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onParticipationSelect && onParticipationSelect(participationId);
  };

  return (
    <div className="card shadow-sm border-0 show-editor-card show-participation-manager">
      <div className="card-header bg-light py-3 show-editor-card__header show-editor-card__header--primary">
        <div className="show-editor-toolbar">
          <div className="show-editor-toolbar__copy">
            <h3 className="h5 mb-0">
              <i className="bi bi-list-check me-2"></i>
              {participationMode === 'draft' ? 'Draft partecipazioni' : 'Partecipazioni live'}
            </h3>
          </div>

          <div className="show-editor-toolbar__actions">
            {participationMode === 'draft' ? (
              <div className="show-editor-inline-toggle" role="group" aria-label="Ordina draft">
                <button
                  type="button"
                  className={`btn btn-sm ${sortMode === 'score' ? 'btn-warning text-dark' : 'btn-outline-warning'}`}
                  onClick={() => setSortMode('score')}
                >
                  Punteggio
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${sortMode === 'name' ? 'btn-light' : 'btn-outline-light'}`}
                  onClick={() => setSortMode('name')}
                >
                  Nome
                </button>
              </div>
            ) : null}

            {canMoveParticipations ? (
              <div className="show-editor-move-tools">
                <select
                  className="form-select form-select-sm"
                  value={moveTargetShowId}
                  onChange={(event) => onMoveTargetChange && onMoveTargetChange(event.target.value)}
                  aria-label="Seleziona la data di destinazione"
                >
                  {moveTargets.map((target) => (
                    <option key={target.id} value={target.id}>
                      {target.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-sm btn-light text-dark"
                  onClick={() => onMoveSelectedParticipations && onMoveSelectedParticipations()}
                  disabled={selectedParticipationsForEdit.length === 0 || !moveTargetShowId || isMovingParticipations || Boolean(editingParticipationId)}
                  title="Sposta le partecipazioni selezionate sulla data scelta"
                >
                  <i className="bi bi-arrow-left-right me-1"></i>
                  {selectedParticipationsForEdit.length > 0
                    ? `Sposta (${selectedParticipationsForEdit.length})`
                    : 'Sposta'}
                </button>
              </div>
            ) : null}

            {selectedParticipationsForEdit.length > 0 && (
              <button
                type="button"
                className="btn btn-sm btn-warning"
                onClick={() => canBulkEditBonuses && onEnableBulkEdit && onEnableBulkEdit()}
                disabled={!canBulkEditBonuses}
                title={canBulkEditBonuses ? 'Aggiungi bonus alle partecipazioni selezionate' : 'I bonus live si attivano solo nelle 48 ore prima dello show'}
              >
                <i className="bi bi-plus-square me-1"></i>
                Bonus ({selectedParticipationsForEdit.length})
              </button>
            )}

            {suggestedBonusCount > 0 ? (
              <span
                className="show-editor-warning-pill"
                title="Ci sono wrestler con bonus o malus titolo suggeriti"
              >
                <span className="show-editor-alert-dot" aria-hidden="true"></span>
                {suggestedBonusCount} da controllare
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="card-body p-0 show-editor-card__body">
        {participations.length === 0 ? (
          <div className="text-center py-5 text-muted show-editor-empty">
            <i className={`bi ${participationMode === 'draft' ? 'bi-pencil-square' : 'bi-inbox'} display-6 d-block mb-2`}></i>
            <p className="mb-0">
              {participationMode === 'draft' ? 'Nessuna bozza partecipazione registrata' : 'Nessuna partecipazione registrata'}
            </p>
          </div>
        ) : (
          <div className="list-group list-group-flush">
            {sortedParticipations.map((participation) => {
              const totalSign = participation.totalPoints >= 0 ? '+' : '';
              const bonusCount = Array.isArray(participation.bonuses) ? participation.bonuses.length : 0;
              const isSelected = selectedParticipationsForEdit.includes(participation.id);
              const hasTitleSuggestion =
                Array.isArray(bonusSuggestionsByWrestler[participation.wrestlerId]) &&
                bonusSuggestionsByWrestler[participation.wrestlerId].length > 0;
              const hasMatchBonus = (participation.bonuses || []).some((value) =>
                MATCH_PRESENCE_BONUS_VALUES.has(normalizeStoredBonusValue(value))
              );
              const bonusLabels = (Array.isArray(participation.bonuses) ? participation.bonuses : []).reduce(
                (acc, storedValue, index) => {
                  const displayBonus = getStoredBonusDisplay(
                    storedValue,
                    BONUS_LOOKUP,
                    showData,
                    Array.isArray(participation.bonusPoints) ? participation.bonusPoints[index] : null
                  );
                  const label = displayBonus?.label || storedValue;
                  const existing = acc.find((item) => item.label === label);

                  if (existing) {
                    existing.qty += 1;
                  } else {
                    acc.push({ label, qty: 1 });
                  }

                  return acc;
                },
                []
              );

              return (
                <div
                  key={participation.id}
                  className={`list-group-item list-group-item-action show-participation-item ${editingParticipationId === participation.id ? 'is-editing' : ''} ${isSelected ? 'is-selected' : ''} ${hasMatchBonus ? 'show-participation-item--match-bonus' : ''}`}
                  role="button"
                  tabIndex={editingParticipationId ? -1 : 0}
                  onClick={() => onParticipationSelect && onParticipationSelect(participation.id)}
                  onKeyDown={(event) => handleSelectableRowKeyDown(event, participation.id)}
                >
                  <div className="show-participation-item__layout">
                    <div className="show-participation-item__content">
                      <div className="show-participation-item__top">
                        <div className="show-participation-item__identity">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={isSelected}
                            onChange={() => onParticipationSelect && onParticipationSelect(participation.id)}
                            onClick={(event) => event.stopPropagation()}
                          />
                          <h6 className="mb-0 fw-bold show-participation-item__name">
                            {participation.wrestlerName}
                            {hasTitleSuggestion ? (
                              <span
                                className="show-editor-alert-dot"
                                title="Questo wrestler ha un bonus o malus titolo suggerito"
                                aria-label="Bonus titolo suggerito"
                              ></span>
                            ) : null}
                          </h6>
                        </div>

                        <div className="show-participation-item__summary">
                          <span className={`show-editor-score-pill ${participation.totalPoints >= 0 ? 'is-positive' : 'is-negative'}`}>
                            {totalSign}{participation.totalPoints}
                          </span>

                          <div className="btn-group btn-group-sm show-participation-item__actions">
                            <button
                              type="button"
                              className="btn btn-outline-primary"
                              onClick={(event) => {
                                event.stopPropagation();
                                onEditParticipation && onEditParticipation(participation);
                              }}
                              title="Modifica"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-danger"
                              onClick={(event) => {
                                event.stopPropagation();
                                onDeleteParticipation && onDeleteParticipation(participation.id);
                              }}
                              title="Elimina"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      {bonusLabels.length > 0 ? (
                        <div className="show-participation-item__bonuses">
                          {bonusLabels.map((bonus) => (
                            <span
                              key={`${participation.id}-${bonus.label}`}
                              className="show-participation-item__bonus-pill"
                            >
                              {bonus.label}
                              {bonus.qty > 1 ? ` x${bonus.qty}` : ''}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {participationMode === 'live' && isLiveBonusLocked && (
        <div className="card-footer bg-light show-editor-footer-note">
          I bonus live si attivano da {liveBonusUnlockLabel || '48 ore prima dello show'}.
        </div>
      )}

      {participationMode === 'draft' && (
        <div className="card-footer bg-light d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
          <small className="text-muted">
            Pubblica le bozze quando vuoi trasformarle nelle partecipazioni definitive.
          </small>
          <button
            type="button"
            className="btn btn-warning"
            onClick={() => onPublishDrafts && onPublishDrafts()}
            disabled={draftCount === 0 || isPublishingDrafts}
            aria-busy={isPublishingDrafts}
          >
            {isPublishingDrafts ? (
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            ) : (
              <i className="bi bi-clipboard-check me-2"></i>
            )}
            {isPublishingDrafts ? 'Pubblicazione...' : 'Pubblica tutte le bozze'}
          </button>
        </div>
      )}
    </div>
  );
}

export default ParticipationManager;
